import Wordmark from "../components/Wordmark";

const LAST_UPDATED = "7 April 2026";
const COMPANY = "Bethany Labs";
const CONTACT_EMAIL = "hello@bethanylabs.com";
const PRODUCT = "YouSift";

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
      <div className="space-y-4 text-gray-400 leading-relaxed">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      <header className="px-6 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <a href="/"><Wordmark size="sm" darkBg={true} /></a>
        <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">← Back</a>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500">Legal</span>
          <h1 className="text-4xl font-black text-white mt-3 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        <Section title="1. Who We Are">
          <p>
            {PRODUCT} is a product of {COMPANY}, a UK-based technology company. When we refer to "we", "us", or "our" in this policy, we mean {COMPANY}.
          </p>
          <p>
            If you have any questions about this Privacy Policy or how we handle your data, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="2. What Data We Collect">
          <p>We collect the minimum data necessary to operate {PRODUCT}. This includes:</p>
          <ul className="space-y-2 mt-2">
            {[
              "YouTube URLs you submit for processing.",
              "An anonymous identifier (UUID) generated in your browser and stored in localStorage. This is not linked to your name, email, or any personally identifiable information.",
              "Your responses to our onboarding quiz, linked only to your anonymous identifier.",
              "Usage data — specifically, the number of videos you have processed.",
              "Payment information, if you purchase a plan. Payment data is handled entirely by Stripe and is never stored on our servers.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4">
            We do not collect your name, email address, or any account credentials unless you contact us directly.
          </p>
        </Section>

        <Section title="3. How We Use Your Data">
          <p>We use the data we collect to:</p>
          <ul className="space-y-2 mt-2">
            {[
              "Process YouTube videos and generate content on your behalf.",
              "Track usage to enforce plan limits and trigger upgrade prompts.",
              "Understand how {PRODUCT} is being used so we can improve it.",
              "Process payments and fulfil your subscription or one-time purchase.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] mt-2 shrink-0" />
                <span>{item.replace("{PRODUCT}", PRODUCT)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4">We do not sell your data. We do not use your data for advertising.</p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>
            {PRODUCT} relies on the following third-party services to operate. Each has its own privacy policy governing how they handle data.
          </p>
          <div className="mt-4 space-y-3">
            {[
              { name: "Anthropic (Claude)", use: "Generates LinkedIn posts and blog articles from your video transcript." },
              { name: "Supadata", use: "Retrieves transcripts from YouTube videos." },
              { name: "AssemblyAI", use: "Fallback transcription service for videos without auto-generated captions." },
              { name: "MongoDB Atlas", use: "Stores your anonymous usage data and generated content." },
              { name: "Stripe", use: "Handles all payment processing. We never see or store your card details." },
              { name: "Vercel", use: "Hosts the YouSift frontend application." },
              { name: "Railway", use: "Hosts the YouSift backend API." },
            ].map((s) => (
              <div key={s.name} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-4 py-3">
                <p className="text-white font-semibold text-sm">{s.name}</p>
                <p className="text-gray-500 text-sm mt-0.5">{s.use}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="5. Data Retention">
          <p>
            Generated content and usage data are retained for as long as your account is active or as needed to provide the service. You may request deletion of your data at any time by contacting us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
          <p>
            Anonymous identifiers stored in your browser (localStorage) can be cleared at any time by clearing your browser data.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>Under UK GDPR, you have the right to:</p>
          <ul className="space-y-2 mt-2">
            {[
              "Access the data we hold about you.",
              "Request correction of inaccurate data.",
              "Request deletion of your data.",
              "Object to how we process your data.",
              "Request that we restrict processing of your data.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4">
            To exercise any of these rights, email us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
            We will respond within 30 days.
          </p>
        </Section>

        <Section title="7. Cookies and Local Storage">
          <p>
            {PRODUCT} does not use tracking cookies. We use your browser's localStorage to store your anonymous user identifier and your onboarding preferences. This data never leaves your device unless explicitly submitted to our API.
          </p>
        </Section>

        <Section title="8. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Continued use of {PRODUCT} after any changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="9. Contact">
          <p>
            For any privacy-related questions or requests, contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </main>

      <footer className="py-8 text-center border-t border-[#1A1A1A]">
        <p className="text-gray-700 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-500 font-semibold">{COMPANY}</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}
